import React, { ChangeEvent, ClipboardEvent, DragEvent, useEffect, useRef, useState } from 'react';
import styled from 'styles/styled-components';
import translations from 'translations';
import { PaperclipIcon } from './icons';
import ImagePreview from './ImagePreview';
import messages from './messages';

export const Wrapper = styled.div<{ isDragOver: boolean; hasError: boolean }>`
  border-radius: 16px;
  overflow: clip;
  display: flex;
  flex-direction: column;
  transition:
    box-shadow 0.2s,
    border-color 0.2s;
  border: 1.5px solid
    ${({ isDragOver, hasError }) => {
      if (hasError) return '#E2483C';
      if (isDragOver) return '#43A047';
      return 'transparent';
    }};
  box-shadow: ${({ isDragOver, hasError }) => {
    if (hasError || isDragOver) return 'none';
    return '0 0 0 1px #E4E7EB';
  }};
  background: ${({ isDragOver }) => (isDragOver ? '#F0FAF1' : '#fff')};
  max-width: 768px;
  margin: 0 auto;

  &:focus-within {
    box-shadow: ${({ hasError }) => (hasError ? 'none' : '0 0 0 1.5px #43a047')};
    border-color: ${({ hasError }) => (hasError ? '#E2483C' : 'transparent')};
  }
`;

export const ContentArea = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 767px) {
    padding: 16px;
    min-height: 100px;
  }
`;

export const ImagesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Textarea = styled.textarea`
  font-family: 'Maven Pro';
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  font-size: 16px;
  color: #111827;
  font-family: inherit;
  line-height: 24px;
  overflow: hidden;
  background: transparent;

  &::placeholder {
    color: #9ca3af;
    font-family: 'Maven Pro';
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #f2f3f5;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px;
  }
`;

export const UploadBtn = styled.button`
  font-family: 'Maven Pro';
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #3c9041;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  padding: 0;

  svg {
    color: #43a047;
  }

  &:hover {
    opacity: 0.8;
  }
`;

export const SupportHint = styled.span<{ mobileOnly?: boolean; desktopOnly?: boolean }>`
  font-size: 14px;
  line-height: 22px;
  color: #4b5563;
  font-family: 'Maven Pro';

  ${({ mobileOnly }) => mobileOnly && `display: none;`}
  ${({ desktopOnly }) => desktopOnly && ``}

  @media (max-width: 767px) {
    ${({ mobileOnly }) => mobileOnly && `display: inline;`}
    ${({ desktopOnly }) => desktopOnly && `display: none;`}
  }
`;

const BelowWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 768px;
  width: 100%;
  margin: 8px auto 0;
`;

const CharCounter = styled.span<{ hasError: boolean }>`
  font-family: 'Maven Pro';
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: ${({ hasError }) => (hasError ? '#e2483c' : '#9ca3af')};
  margin-left: auto;
`;

const ErrorText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #e2483c;
  margin: 0;
  padding: 0;
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

const MAX_TEXT_LENGTH = 1000;

interface ImageFile {
  id: string;
  file: File;
  previewUrl: string;
}

interface QuickOrderTextAreaProps {
  text: string;
  images: ImageFile[];
  errorMessage?: string | null;
  onTextChange: (value: string) => void;
  onFilesAdded: (files: File[]) => void;
  onImageRemove: (id: string) => void;
}

const MOBILE_MQ = typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)') : null;

const QuickOrderTextArea = ({
  text,
  images,
  errorMessage,
  onTextChange,
  onFilesAdded,
  onImageRemove,
}: QuickOrderTextAreaProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isMobile, setIsMobile] = useState(() => MOBILE_MQ?.matches ?? false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!MOBILE_MQ) return;
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    MOBILE_MQ.addEventListener('change', handler);
    return () => MOBILE_MQ.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  const handlePaste = (e: ClipboardEvent) => {
    const imageItems = Array.from(e.clipboardData.items).filter((item) => item.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    onFilesAdded(imageItems.map((item) => item.getAsFile()).filter(Boolean) as File[]);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    onFilesAdded(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    onFilesAdded(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => onTextChange(e.target.value);

  const isOverLimit = text.length > MAX_TEXT_LENGTH;
  const hasError = !!errorMessage || isOverLimit;

  return (
    <>
      <Wrapper
        isDragOver={isDragOver}
        hasError={hasError}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onPaste={handlePaste}
      >
        <ContentArea>
          {images.length > 0 && (
            <ImagesRow>
              {images.map((img) => (
                <ImagePreview key={img.id} id={img.id} previewUrl={img.previewUrl} onRemove={onImageRemove} />
              ))}
            </ImagesRow>
          )}
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            placeholder={translations(
              images.length
                ? messages.inputPlaceholderWithImage
                : isMobile
                  ? messages.inputPlaceholderMobile
                  : messages.inputPlaceholder,
            )}
            rows={3}
          />
        </ContentArea>
        <Divider />
        <Footer>
          <UploadBtn type="button" onClick={() => fileInputRef.current?.click()}>
            <PaperclipIcon /> {translations(messages.uploadImage)}
          </UploadBtn>
          <SupportHint desktopOnly>{translations(messages.supportHint)}</SupportHint>
          <SupportHint mobileOnly>{translations(messages.supportHintMobile)}</SupportHint>
        </Footer>
      </Wrapper>
      <BelowWrapper>
        {errorMessage ? <ErrorText>{errorMessage}</ErrorText> : <span />}
        <CharCounter hasError={isOverLimit}>
          {text.length} / {MAX_TEXT_LENGTH}
        </CharCounter>
      </BelowWrapper>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        multiple
        onChange={handleFileInput}
      />
    </>
  );
};

export default QuickOrderTextArea;
